import { useContext, useEffect, useState, useMemo } from 'react';
import { Divider, Box, ListItem, Typography } from '@mui/material';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import SupportOutlinedIcon from '@mui/icons-material/SupportOutlined';
import PolicyOutlinedIcon from '@mui/icons-material/PolicyOutlined';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
// Our components
import { AdminRouteSidebar, SuperAdminRouter, UserRouteSidebar, GiftUserRouteSidebar } from './Sidebar.router';
import { AuthContext } from 'context/auth-context';
import { KYXContext } from 'context/kyx-context';
// Utils and Helpers
import {
    BUSINESS_FUNDING_TYPE,
    KYB_STATUS,
    KYC_STATUS,
    PAYSTAND_SUPPORT_URL,
    USER_ROLES,
    PAYSTAND_FAQ_URL
} from 'helpers/constants';
import { isAdminUser } from 'helpers/auth';
// Assets
import { useStyles } from './styles';
import './styles.css';

export const SidebarMenu = () => {
    const history = useHistory();
    const classes = useStyles();
    const { userInfo, businessInfo } = useContext(AuthContext);
    const { setIsKYXModalOpen } = useContext(KYXContext);
    const [hidden, setHidden] = useState(window.localStorage.getItem('kyx_hidden') === 'true');
    const [kybStatus, setKYBStatus] = useState({
        bgColor: '',
        lblColor: '',
        label: '',
        status: ''
    });

    const getRoutesByUserRole = () => {
        if (userInfo?.role?.id === USER_ROLES.CARD_HOLDER) {
            if (userInfo.is_giftcard_only === true) {
                return GiftUserRouteSidebar;
            }
            return UserRouteSidebar;
        }
        if (userInfo?.role?.id === USER_ROLES.ADMIN) {
            return AdminRouteSidebar;
        }
        if (userInfo?.role?.id === USER_ROLES.SUPERADMIN) {
            return SuperAdminRouter;
        }
        return UserRouteSidebar;
    };

    const hasKYC = useMemo(() => Object.values(KYC_STATUS).includes(userInfo.kyc), [userInfo.kyc]);

    const hasKYB =
        !!userInfo.kyb &&
        businessInfo.owners?.length > 0 &&
        (businessInfo.banks?.length > 0 || businessInfo.funding_type === BUSINESS_FUNDING_TYPE.PREFUNDING);

    useEffect(() => {
        const KYBDate = dayjs(userInfo.kyb_date).format('MM/DD/YYYY');
        if (userInfo.kyb === KYB_STATUS.APPROVED && !userInfo.account) {
            if (
                businessInfo.owners?.length > 0 &&
                (businessInfo.banks?.length > 0 || businessInfo.funding_type === BUSINESS_FUNDING_TYPE.PREFUNDING)
            ) {
                setKYBStatus({
                    bgColor: '#F8EBDD',
                    lblColor: '#FF9E43',
                    status: 'UNDER REVIEW',
                    label: 'Submitted on ' + KYBDate
                });
                return;
            }
            setKYBStatus({
                bgColor: '#F1F3F7',
                lblColor: '#7D95A5',
                status: 'INCOMPLETE',
                label: 'Complete Now'
            });
            return;
        }
        if (!hasKYC) {
            setKYBStatus({
                bgColor: '#F1F3F7',
                lblColor: '#7D95A5',
                status: 'INCOMPLETE',
                label: 'Complete Now'
            });
            return;
        }
        switch (userInfo.kyc) {
            case KYC_STATUS.DECLINED:
                setKYBStatus({
                    bgColor: '#FBD8E3',
                    lblColor: '#F65570',
                    status: 'DECLINED',
                    label: 'Contact Support'
                });
                return;
            default:
                break;
        }
        switch (userInfo.kyb) {
            case KYB_STATUS.NOT_SUBMITTED:
                setKYBStatus({
                    bgColor: '#F1F3F7',
                    lblColor: '#7D95A5',
                    status: 'INCOMPLETE',
                    label: 'Complete Now'
                });
                break;
            case KYB_STATUS.NOT_STARTED:
                setKYBStatus({
                    bgColor: '#F1F3F7',
                    lblColor: '#7D95A5',
                    status: 'INCOMPLETE',
                    label: 'Complete Now'
                });
                break;
            case KYB_STATUS.STARTED:
                setKYBStatus({
                    bgColor: '#F8EBDD',
                    lblColor: '#FF9E43',
                    status: 'STARTED',
                    label: 'Finish '
                });
                break;
            case KYB_STATUS.INCOMPLETE:
                setKYBStatus({
                    bgColor: '#F1F3F7',
                    lblColor: '#7D95A5',
                    status: 'INCOMPLETE',
                    label: 'Complete Now'
                });
                break;
            case KYB_STATUS.DECLINED:
                setKYBStatus({
                    bgColor: '#FBD8E3',
                    lblColor: '#F65570',
                    status: 'DECLINED',
                    label: 'Contact Support'
                });
                break;
            case KYB_STATUS.APPROVED:
                setKYBStatus({
                    bgColor: '#E2EFD8',
                    lblColor: '#77B44B',
                    status: 'VERIFIED',
                    label: 'Hide'
                });
                break;
            case KYB_STATUS.REVIEW:
                setKYBStatus({
                    bgColor: '#F8EBDD',
                    lblColor: '#FF9E43',
                    status: 'UNDER REVIEW',
                    label: 'Submitted on ' + KYBDate
                });
                break;
            default:
                setKYBStatus({
                    bgColor: '#F1F3F7',
                    lblColor: '#7D95A5',
                    status: 'INCOMPLETE',
                    label: 'Complete Now'
                });
                break;
        }
    }, [userInfo, hasKYB, hasKYC, businessInfo.owners?.length, businessInfo.banks?.length]);

    const handleBannerClick = () => {
        if (userInfo.kyc === KYC_STATUS.DECLINED) {
            window.open(PAYSTAND_SUPPORT_URL, '_blank');
            return;
        }
        if (!hasKYB) {
            setIsKYXModalOpen(true);
            return;
        }
        if (userInfo.kyb === KYB_STATUS.APPROVED) {
            if (!userInfo.account) return;

            window.localStorage.setItem('kyx_hidden', 'true');
            setHidden(true);
            return;
        }
        if (userInfo.kyb === KYB_STATUS.REVIEW) return;

        switch (userInfo.kyb) {
            case KYB_STATUS.DECLINED:
                window.open(PAYSTAND_SUPPORT_URL, '_blank');
                break;
            case KYB_STATUS.APPROVED:
                window.localStorage.setItem('kyx_hidden', 'true');
                setHidden(true);
                break;
            default:
                setIsKYXModalOpen(true);
                break;
        }
    };

    return (
        <Box className="sidebarContainer">
            <Box className="sidebarMenu">
                {getRoutesByUserRole().map((route, index) => (
                    <Box
                        key={index}
                        marginTop={index > 0 && '6px'}
                        className={history.location.pathname === route.url ? classes.itemSelected : classes.sidebarItem}
                        {...(route.url === '/cards' ? { 'data-testid': 'cards-section' } : {})}>
                        <Box
                            width="4px"
                            backgroundColor={history.location.pathname === route.url ? '#1BABE2' : null}
                            height="40px"
                        />
                        <Box marginLeft="18px" display="flex" flexDirection="column" justifyContent="center">
                            <route.icon sx={{ width: '20px' }}></route.icon>
                        </Box>
                        <Divider />
                        <ListItem
                            sx={{
                                cursor: 'pointer',
                                borderTopLeftRadius: '5px',
                                borderBottomLeftRadius: '5px',
                                marginLeft: '12px'
                            }}
                            onClick={() => history.push(route.url)}>
                            <Typography variant={history.location.pathname === route.url ? 'selectedSidebarItem' : 'sidebarItem'}>
                                {route.name}
                            </Typography>
                        </ListItem>
                        <Divider />
                    </Box>
                ))}
            </Box>
            {userInfo?.role?.id !== USER_ROLES.SUPERADMIN && (
                <Box className="sidebarFooter">
                    {isAdminUser(userInfo?.role?.id) && userInfo.is_business_creator ? (
                        (!hasKYC || !hasKYB || (hasKYB && !userInfo.account) || (hasKYB && userInfo.account && !hidden)) &&
                        businessInfo.loaded ? (
                            <Box className="sidebarItem">
                                <Box
                                    sx={{
                                        background: 'radial-gradient(140.11% 110.99% at 50% 100%, #172D54 0%, #215283 100%)',
                                        width: {
                                            xs: '14em',
                                            sm: '14em',
                                            md: '170px',
                                            lg: '170px'
                                        },
                                        height: {
                                            xs: '12em',
                                            sm: '12em',
                                            md: '170px',
                                            lg: '170px'
                                        },
                                        marginBottom: '2em',
                                        borderRadius: '4px',
                                        display: hidden ? 'none' : 'initial'
                                    }}>
                                    <Typography
                                        variant="selectedSidebarItem"
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            padding: '16px 16px 5px 16px'
                                        }}>
                                        KYB/KYC
                                    </Typography>
                                    <Typography
                                        variant="selectedSidebarItem"
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            paddingBottom: '.5em'
                                        }}>
                                        Application Status
                                    </Typography>
                                    <Box
                                        sx={{
                                            margin: '1em',
                                            padding: '8px 4px',
                                            borderRadius: '4px',
                                            backgroundColor: kybStatus.bgColor,
                                            display: 'flex',
                                            justifyContent: 'center'
                                        }}>
                                        <Typography
                                            sx={{
                                                fontFamily: 'Montserrat',
                                                fontStyle: 'normal',
                                                fontWeight: 700,
                                                fontSize: '12px',
                                                lineHeight: '16px',
                                                color: kybStatus.lblColor
                                            }}>
                                            {kybStatus.status}
                                        </Typography>
                                    </Box>
                                    <hr style={{ border: '1px solid #7D95A5' }} />
                                    <Typography
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '12px',
                                            cursor: 'pointer',
                                            padding: '14px 8px'
                                        }}
                                        onClick={() => handleBannerClick()}>
                                        {kybStatus.label}
                                    </Typography>
                                </Box>
                            </Box>
                        ) : null
                    ) : null}

                    <Box marginLeft="24px" marginBottom="6px" className={classes.sidebarItem}>
                        <Box display="flex" flexDirection="column" justifyContent="center">
                            <HelpOutlineIcon fontSize="small" />
                        </Box>
                        <ListItem
                            className={classes.sidebarItem}
                            sx={{
                                cursor: 'pointer',
                                borderTopLeftRadius: '5px',
                                borderBottomLeftRadius: '5px',
                                marginLeft: '12px'
                            }}
                            onClick={() => window.open(PAYSTAND_FAQ_URL)}>
                            FAQs
                        </ListItem>
                        <Divider />
                    </Box>
                    <Box marginLeft="24px" marginBottom="6px" className={classes.sidebarItem}>
                        <Box display="flex" flexDirection="column" justifyContent="center">
                            <FeedbackOutlinedIcon fontSize="small" />
                        </Box>
                        <ListItem
                            className={classes.sidebarItem}
                            sx={{
                                cursor: 'pointer',
                                borderTopLeftRadius: '5px',
                                borderBottomLeftRadius: '5px',
                                marginLeft: '12px'
                            }}
                            onClick={() => window.open('https://www.paystand.com/corporate-card-feedback')}>
                            Feedback
                        </ListItem>
                        <Divider />
                    </Box>
                    <Box marginLeft="24px" marginBottom="6px" className={classes.sidebarItem}>
                        <Box display="flex" flexDirection="column" justifyContent="center">
                            <SupportOutlinedIcon fontSize="small" />
                        </Box>
                        <ListItem
                            className={classes.sidebarItem}
                            sx={{
                                cursor: 'pointer',
                                borderTopLeftRadius: '5px',
                                borderBottomLeftRadius: '5px',
                                marginLeft: '12px'
                            }}
                            onClick={() => window.open(PAYSTAND_SUPPORT_URL)}>
                            Support
                        </ListItem>
                    </Box>
                    <Box marginLeft="24px" marginBottom="6px" className={classes.sidebarItem}>
                        <Box display="flex" flexDirection="column" justifyContent="center">
                            <PolicyOutlinedIcon fontSize="small" />
                        </Box>
                        <ListItem
                            className={classes.sidebarItem}
                            sx={{
                                cursor: 'pointer',
                                borderTopLeftRadius: '5px',
                                borderBottomLeftRadius: '5px',
                                marginLeft: '12px'
                            }}
                            onClick={() => window.open('https://www.paystand.com/corporate-card-terms-of-service')}>
                            Legal
                        </ListItem>
                    </Box>
                </Box>
            )}
        </Box>
    );
};
